import React from 'react';
import css from 'styled-jsx/css';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { colors, fonts, dimensions } from '../Theme';

const labelStyles = css`
  .form__label {
    padding-bottom: 10px;
    opacity: 0.7;
    font-family: ${fonts.primaryFont};
    font-size: 14px;
    color: ${colors.ywgNavy};
    cursor: pointer;
  }
  .form__label--required::after {
    content: '*';
    font-family: ${fonts.primaryFont};
    font-size: 14px;
    color: ${colors.ywgPinkishOrange};
    padding-right: 5px;
    margin-left: 0.125rem;
  }
`;

const Label = ({ children, required, ...props }) => {
  const labelClass = classnames('form__label', {
    'form__label--required': required
  });
  return (
    <label className={labelClass} {...props}>
      {children}
      <style jsx>{labelStyles}</style>
    </label>
  );
};

const inputStyles = css`
  .form__input {
    border: 2px solid ${colors.ywgVeryLightBlue};
    padding: 15px 0 15px 20px;
    margin-bottom: ${dimensions.ywgFormSpacing};
    width: auto;
  }
  .form__input:focus {
    outline: solid 2px ${colors.ywgPinkishOrange};
  }
  input[type='date' i] {
    background-color: white;
  }
  input[type='date']::-webkit-datetime-edit,
  .form__input::placeholder {
    opacity: 0.7;
    font-style: italic;
  }
  input[type='date'].form__input--hasInput::-webkit-datetime-edit {
    opacity: 1;
    font-style: initial;
  }
  textarea {
    min-height: 50px;
  }
  .form__input--invalid {
    border: 2px solid red;
    margin-bottom: 0;
  }
`;

const Input = React.forwardRef(({ valid, value, className, ...props }, ref) => {
  const inputClass = classnames('form__input', className, {
    'form__input--invalid': !valid,
    'form__input--hasInput': value && value.trim().length > 0
  });
  return (
    <>
      {props.type && props.type === 'textarea' ? (
        <textarea className={inputClass} value={value} {...props} />
      ) : (
        <input ref={ref} className={inputClass} value={value} {...props} />
      )}
      <style jsx>{inputStyles}</style>
    </>
  );
});

Input.displayName = 'Input';

const errorStyles = css`
  .form__error {
    margin-bottom: 4px;
    min-height: 1rem;
    padding-left: 20px;
    color: red;
    font-family: ${fonts.secondaryFont};
    font-size: 0.875rem;
    font-style: italic;
  }
`;

const Error = ({ valid, error }) => {
  if (valid || !error) return null;
  else
    return (
      <span className="form__error">
        {error.error || error}
        <style jsx>{errorStyles}</style>
      </span>
    );
};

const FormControl = React.forwardRef(
  ({ name, required, label, valid, error, ...props }, ref) => {
    return (
      <>
        <Label htmlFor={name} required={required}>
          {label}
        </Label>
        <Input
          id={name}
          required={required}
          name={name}
          valid={valid}
          ref={ref}
          {...props}
        />
        <Error valid={valid} error={error} />
      </>
    );
  }
);

FormControl.displayName = 'FormControl';

FormControl.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  valid: PropTypes.bool
};

export default FormControl;
export { Input, Label, Error };
